import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Send, X } from "lucide-react";

export default function VoiceRecorder({ open, onClose, onSubmit }) {
  const [recording, setRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [preview, setPreview] = useState(null); // { blob, dataUrl, duration }
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAt = useRef(0);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (!open) {
      cleanup();
    }
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const cleanup = () => {
    clearInterval(timerRef.current);
    setRecording(false);
    setDuration(0);
    setPreview(null);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      mediaRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        const reader = new FileReader();
        reader.onloadend = () => setPreview({ dataUrl: reader.result, duration });
        reader.readAsDataURL(blob);
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
      };
      rec.start();
      startedAt.current = Date.now();
      setRecording(true);
      setDuration(0);
      timerRef.current = setInterval(() => {
        const d = (Date.now() - startedAt.current) / 1000;
        setDuration(d);
        if (d >= 30) stop();
      }, 100);
    } catch (e) {
      alert("Mikrofon izni gerekli: " + e.message);
    }
  };

  const stop = () => {
    clearInterval(timerRef.current);
    setRecording(false);
    try { mediaRef.current?.stop(); } catch {}
  };

  const submit = () => {
    if (preview) onSubmit(preview.dataUrl, preview.duration);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="voice-dialog">
      <div className="bg-white border-hard shadow-hard-lg rounded-2xl p-6 w-full max-w-sm slide-up">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl">Sesli not</h2>
          <button onClick={onClose} data-testid="voice-close-btn" className="w-9 h-9 border-hard-2 rounded-lg flex items-center justify-center press-btn">
            <X strokeWidth={3} className="w-4 h-4" />
          </button>
        </div>

        <div className="flex flex-col items-center gap-4">
          <div className={`w-28 h-28 rounded-full border-hard shadow-hard flex items-center justify-center ${recording ? "bg-[#FF3B30] text-white animate-pulse" : "bg-[#CCFF00]"}`}>
            <Mic strokeWidth={3} className="w-10 h-10" />
          </div>
          <div className="text-3xl font-display" data-testid="voice-timer">{duration.toFixed(1)}s</div>

          {!recording && !preview && (
            <button onClick={start} data-testid="voice-start-btn" className="bg-[#CCFF00] border-hard shadow-hard rounded-xl px-5 py-3 font-black press-btn">
              Kayda başla
            </button>
          )}
          {recording && (
            <button onClick={stop} data-testid="voice-stop-btn" className="bg-[#FF3B30] text-white border-hard shadow-hard rounded-xl px-5 py-3 font-black press-btn flex items-center gap-2">
              <Square strokeWidth={3} className="w-4 h-4" /> Durdur
            </button>
          )}
          {preview && !recording && (
            <>
              <audio controls src={preview.dataUrl} className="w-full" data-testid="voice-preview-audio" />
              <div className="flex gap-2 w-full">
                <button onClick={() => setPreview(null)} data-testid="voice-retry-btn" className="flex-1 bg-white border-hard rounded-xl px-4 py-2 font-black press-btn">
                  Tekrar
                </button>
                <button onClick={submit} data-testid="voice-submit-btn" className="flex-1 bg-[#CCFF00] border-hard shadow-hard rounded-xl px-4 py-2 font-black press-btn flex items-center justify-center gap-2">
                  <Send strokeWidth={3} className="w-4 h-4" /> Gönder
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
